import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { HttpRequestService } from './http-request.service';
import { ReadOnlyService } from './read-only.service';

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private permissionsSubject = new BehaviorSubject<string[]>([]);
  permissions$ = this.permissionsSubject.asObservable();

  constructor(
    private http: HttpRequestService,
    private readOnlyService: ReadOnlyService
  ) { }

  /**
   * Fetches the latest permissions from the API and updates the store.
   * Returns a Promise so it can be awaited in APP_INITIALIZER or called
   * anytime a refresh is needed (e.g. after role/company switch).
   */
  loadPermissions(): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) {
      this.permissionsSubject.next([]);
      this.readOnlyService.setPermissions([]);
      return Promise.resolve();
    }

    return this.http.request('get', '/user/get-permissions/', null).pipe(
      catchError(() => {
        this.readOnlyService.markPermissionsFailed();
        return of({ permissions: [] });
      })
    ).toPromise().then((response: any) => {
      const permissions = response?.data?.permission || response?.data?.permissions || (Array.isArray(response?.data) ? response.data : null);
      if (Array.isArray(permissions)) {
        this.permissionsSubject.next(permissions); // emit fresh permissions
        this.readOnlyService.setPermissions(permissions);
      } else {
        this.readOnlyService.setPermissions([]);
      }
    }).catch(() => {
      this.readOnlyService.markPermissionsFailed();
    });
  }

  setPermissions(permissions: string[]): void {
    this.permissionsSubject.next(permissions);
    this.readOnlyService.setPermissions(permissions);
  }

  clearPermissions(): void {
    this.permissionsSubject.next([]);
    this.readOnlyService.setPermissions([]);
  }

  getPermissions(): string[] {
    return this.permissionsSubject.getValue();
  }

  hasPermission(permission: string | string[]): boolean {
    if (!permission) return true;
    const perms = this.permissionsSubject.getValue();
    if (!perms || perms.length === 0) return false;
    const required = Array.isArray(permission) ? permission : [permission];
    return perms.some((p: any) => {
      const pName = typeof p === 'string' ? p : p?.permission_name;
      return required.includes(pName);
    });
  }

  isReadOnly(): boolean {
    return this.readOnlyService.isReadOnly();
  }

  isComponentReadOnly(feature: string | string[]): boolean {
    return this.readOnlyService.isComponentReadOnly(feature);
  }
}